import { formatToHumanReadable } from "@/lib/format-data";

import { CallFetchParams } from "@/types/call";
import { CallDirection, CallStatus } from "@/types/enums/call";

import {
  type FilterDefinition,
  LOCATION_SCOPE_DEFAULT,
  booleanFilter,
  defineFilters,
  entitySetFilter,
  enumFilter,
  enumSetFilter,
  numberFilter,
  textFilter,
} from "@/features/table-filters/contract";
import type { FilterKey } from "@/features/table-filters/contract";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isValidDate(value: string): boolean {
  if (!DATE_PATTERN.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

function normalizeDateRange(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || value.length !== 2) return undefined;
  const [start, end] = value;
  if (typeof start !== "string" || typeof end !== "string") return undefined;
  if (!isValidDate(start) || !isValidDate(end)) return undefined;
  if (start > end) return undefined;
  return [start, end];
}

function repeatedValues(params: URLSearchParams, key: string): string[] {
  return params.getAll(key);
}

function replaceRepeated(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | undefined,
): void {
  params.delete(key);
  values?.forEach((value) => params.append(key, value));
}

function dateRangeArrayFilter<Request extends object>({
  key,
  label,
  queryKey,
}: {
  key: FilterKey<Request>;
  label: string;
  queryKey: string;
}): FilterDefinition<Request> {
  return {
    key,
    requestKeys: [key],
    urlKeys: [key],
    label,
    value: "dateRange",
    operator: "between",
    control: "dateRange",
    contract: { normalize: normalizeDateRange },
    urlPolicy: "boundedSet",
    presented: true,
    query: { keys: [queryKey], policy: "repeated" },
    normalizeRequest(request) {
      const next = { ...(request as Record<string, unknown>) };
      const value = normalizeDateRange(next[key]);
      if (value) next[key] = value;
      else delete next[key];
      return next as Partial<Request>;
    },
    writeQuery(params, request) {
      replaceRepeated(
        params,
        queryKey,
        normalizeDateRange((request as Record<string, unknown>)[key]),
      );
    },
    parseUrl(params) {
      const value = normalizeDateRange(repeatedValues(params, key));
      return value ? ({ [key]: value } as Partial<Request>) : {};
    },
    writeUrl(params, request) {
      replaceRepeated(
        params,
        key,
        normalizeDateRange((request as Record<string, unknown>)[key]),
      );
    },
    readControl(request) {
      return normalizeDateRange((request as Record<string, unknown>)[key]);
    },
    writeControl(request, value) {
      return { ...request, [key]: value } as Partial<Request>;
    },
  };
}

const statusOptions = Object.values(CallStatus).map((status) => ({
  value: status,
  label: formatToHumanReadable(status),
}));

const directionOptions = Object.values(CallDirection).map((direction) => ({
  value: direction,
  label: formatToHumanReadable(direction),
}));

function buildCallFilterDefinitions({
  includeIdInUrl,
  includePagination,
}: {
  includeIdInUrl: boolean;
  includePagination: boolean;
}) {
  return defineFilters<CallFetchParams>()([
    ...(includePagination
      ? [
          numberFilter<CallFetchParams>({
            key: "page",
            label: "Page",
            presented: false,
            queryKey: "page",
          }),
          numberFilter<CallFetchParams>({
            key: "perPage",
            label: "Per page",
            presented: false,
            queryKey: "per_page",
          }),
        ]
      : []),
    textFilter<CallFetchParams>({
      key: "sortBy",
      label: "Sort by",
      presented: false,
      queryKey: "sort_by",
    }),
    enumFilter<CallFetchParams>({
      key: "sortDirection",
      label: "Sort direction",
      presented: false,
      queryKey: "sort_direction",
      options: [
        { value: "asc", label: "Ascending" },
        { value: "desc", label: "Descending" },
      ],
    }),
    dateRangeArrayFilter<CallFetchParams>({
      key: "createdDate",
      label: "Date",
      queryKey: "created_date",
    }),
    textFilter<CallFetchParams>({
      key: "userName",
      label: "Name",
      operator: "contains",
      queryKey: "user_name",
    }),
    textFilter<CallFetchParams>({
      key: "userId",
      label: "Patient ID",
      queryKey: "user_id",
    }),
    textFilter<CallFetchParams>({
      key: "phoneNumber",
      label: "Phone Number",
      operator: "contains",
      queryKey: "phone_number",
    }),
    textFilter<CallFetchParams>({
      key: "id",
      label: "Call ID",
      urlPolicy: includeIdInUrl ? "scalar" : "none",
      queryKey: "id",
    }),
    enumFilter<CallFetchParams>({
      key: "direction",
      label: "Direction",
      control: "select",
      options: directionOptions,
    }),
    enumSetFilter<CallFetchParams>({
      key: "status",
      label: "Status",
      options: statusOptions,
    }),
    entitySetFilter<CallFetchParams>({
      key: "tags",
      label: "Tags",
      queryKey: "tags",
    }),
    entitySetFilter<CallFetchParams>({
      key: "locationIds",
      label: "Location",
      control: "location",
      queryKey: "location_ids",
      defaultPolicy: LOCATION_SCOPE_DEFAULT,
    }),
    booleanFilter<CallFetchParams>({
      key: "hasTasks",
      label: "With tasks",
      presented: false,
      queryKey: "has_tasks",
    }),
    textFilter<CallFetchParams>({
      key: "tz",
      label: "Timezone",
      presented: false,
      queryKey: "tz",
    }),
  ]);
}

export const callInboxFilterDefinitions = buildCallFilterDefinitions({
  includeIdInUrl: false,
  includePagination: false,
});

export const callTableFilterDefinitions = buildCallFilterDefinitions({
  includeIdInUrl: true,
  includePagination: true,
});

export const callApiFilterDefinitions = callTableFilterDefinitions;
